// Assistant-driven refetch (@adr SPARK-0024-assistant-driven-refetch-is-mutation-level@1). When an Assistant turn runs a
// data-changing tool, the agent-service emits a mid-stream `tool_call` event
// carrying the tool name. AssistantDrawer collects those names over the turn
// and, on `done`, refetches the query operations mapped here. Apollo's
// normalized-cache diff then re-renders only the panes whose data changed —
// so this map is tool-level, not field-level.

// EDIT HERE — agent TOOL name (the apollo-mcp operation name emitted on
// tool_call, e.g. "UpdateMyProfile") -> Spark query operation names to refetch.
// ONLY tools listed here trigger a refetch. The agent's read-only tools
// (SearchPersons, GetMatchDetail, MySelf, Countries, …) are intentionally
// absent — we never refetch on a generic/read-only tool call.

// CV entries (Experience/Education/Expertise/Certificate/LanguageSkill) are
// selected by both Myself and MyselfExpertiseData; refetchQueries only re-runs
// whichever is currently mounted, so listing both is safe.
const CV = ["Myself", "MyselfExpertiseData"];

// Save/Community (contacts & contact groups). Adding/removing a contact flips
// isContact (MyContacts), changes the count (MyContactCount), and — when groups
// are involved — a group's itemCount (MyPersonGroups) and a contact's group
// pills (PersonGroupMemberships). refetchQueries only re-runs whichever of these
// is currently mounted, so listing all four is safe.
const CONTACTS = ["MyContacts", "MyContactCount", "MyPersonGroups", "PersonGroupMemberships"];

const RULES: Record<string, string[]> = {
  UpdateMyProfile: ["Myself"],

  CreateExperience: CV, UpdateExperience: CV, DeleteExperience: CV,
  CreateEducation: CV, UpdateEducation: CV, DeleteEducation: CV,
  CreateExpertise: CV, UpdateExpertise: CV, DeleteExpertise: CV,
  CreateCertificate: CV, UpdateCertificate: CV, DeleteCertificate: CV,
  CreateLanguageSkill: CV, UpdateLanguageSkill: CV, DeleteLanguageSkill: CV,

  CreateMatch: ["MatchList"],
  CreateMatchCandidates: ["MatchCandidateList"],
  DeleteMatchCandidates: ["MatchCandidateList"],
  // LockMatchCandidate is the person-keyed lock/unlock the model now emits; it resolves person →
  // candidate id server-side and drives UpdateMatchCandidate internally, so the model never emits
  // UpdateMatchCandidate as a tool itself — no entry for it.
  LockMatchCandidate: ["MatchCandidateList"],

  // Agent design-field writes (genesis#167/#168) change match.design, which the editor reads via
  // MatchDetail — refetch reflects the change when the editor isn't holding unsaved local edits for
  // this match; concurrent agent-write + open dirty editor is out of scope (see #167).
  SetMatchTeamSize: ["MatchDetail"],
  SetMatchNotes: ["MatchDetail"],
  SetMatchConditions: ["MatchDetail"],
  // The confirmed-apply half of the requirements proposal flow
  // (smycles/development/services/agent-service#95). It is a zero-arg agent-service tool that writes
  // through SetMatchConditions internally, so its own name is the only tool_call frame spark sees —
  // without an entry here a landed write never reaches the open editor.
  ApplyProposedRequirements: ["MatchDetail"],
  SetListingConditions: ["Listing", "ListingMatchRequirements"],
  ApplyListingRequirements: ["Listing", "ListingMatchRequirements"],
  SetListingCompanyConditions: ["Listing", "ListingMatchRequirements"],
  SetMatchCompanyConditions: ["MatchDetail"],
  SetMatchProject: ["MatchDetail"],
  // SetMatchName writes the match's own name (Match.name, the editor heading), not the design blob,
  // but the editor still reads it via MatchDetail — refetch so the heading reflects the rename.
  SetMatchName: ["MatchDetail"],

  // Contacts: membership + count + group itemCounts + per-contact group pills.
  AddContacts: CONTACTS, RemoveContacts: CONTACTS,
  // Contact groups: the group list, plus the per-contact group pills on rename/delete.
  CreateContactGroup: ["MyPersonGroups"],
  UpdateContactGroup: ["MyPersonGroups", "PersonGroupMemberships"],
  DeleteContactGroup: ["MyPersonGroups", "PersonGroupMemberships"],
};

// Deduped query operation names to refetch for the turn's tool calls. Unmapped
// tools contribute nothing (debug-logged, so a forgotten data-changing tool is
// noticeable). Empty array => no refetch.
export function queriesFor(tools: string[]): string[] {
  const out = new Set<string>();
  for (const name of tools) {
    const queries = RULES[name];
    if (!queries) {
      console.debug(`[agent-refresh] tool "${name}" not in refetch map — ignored`);
      continue;
    }
    queries.forEach((q) => out.add(q));
  }
  return [...out];
}
